if (!customElements.get('appointment-booking-form')) {
  class AppointmentBookingForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form#AppointmentBookingForm');
      if (!this.form) throw new Error('Form element does not exist');
      
      this.storesUrl = this.dataset.storesUrl;
      this.apiEndpoint = this.dataset.apiEndpoint || 'https://scervino.appshop.service.triboo.it/api/boutique/insert';
      this.countryCode = this.dataset.countryCode || 'US';
      this.stores = [];
      this.selectedStore = null;
      this.flatpickrInstance = null;
      
      this.storeSelect = this.form.querySelector('#AppointmentForm-StoreName');
      this.storeAddressInput = this.form.querySelector('#AppointmentForm-StoreAddress');
      this.dateInput = this.form.querySelector('#AppointmentForm-Date');
      this.timeSelect = this.form.querySelector('#AppointmentForm-Time');
      this.formErrors = this.querySelector('#formErrors');
      this.formSuccess = this.querySelector('#formSuccess');
      this.submitBtn = this.form.querySelector('button[type="submit"]');
    }

    async init() {
      await this.loadStores();
      this.setupStoreSelector();
      this.setupDateSelector();
      this.setupTimeSelector();
      this.setupFormSubmit();
      this.setupValidation();
      // Save original submit button text
      if (this.submitBtn) {
        this.submitBtn.dataset.originalText = this.submitBtn.textContent;
      }
      // Initialize time select with "select date first" message
      this.showSelectDateFirstMessage();
    }

    async loadStores() {
      try {
        const response = await fetch(this.storesUrl);
        if (!response.ok) {
          throw new Error(`Failed to load stores: ${response.statusText}`);
        }
        this.stores = await response.json();
        this.populateStoreSelect();
      } catch (error) {
        console.error('Error loading stores:', error);
        this.showError(window.translationStrings?.errorLoadStores || 'Failed to load store information. Please refresh the page.');
      }
    }

    populateStoreSelect() {
      if (!this.storeSelect) return;
      
      // this.storeSelect is already the <select> element
      const select = this.storeSelect;
      if (!select || select.tagName !== 'SELECT') return;

      // Store current value to preserve selection if still valid
      const currentValue = select.value;

      // Get placeholder text from existing option
      const existingPlaceholder = select.querySelector('option[value=""]');
      const placeholderText = existingPlaceholder ? existingPlaceholder.textContent : 'Select a store';

      // Clear existing options
      select.innerHTML = '';

      // Add placeholder option
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = placeholderText;
      select.appendChild(placeholder);

      // Add store options
      this.stores.forEach(store => {
        const option = document.createElement('option');
        option.value = store.store_name;
        option.textContent = store.store_name;
        // Preserve selection if still valid
        if (store.store_name === currentValue) {
          option.selected = true;
        }
        select.appendChild(option);
      });

      // If current value is no longer valid, reset to placeholder
      if (currentValue && !this.stores.some(s => s.store_name === currentValue)) {
        select.value = '';
      }

      // Update custom dropdown if it exists and is initialized
      const dropdownInput = select.closest('dropdown-input');
      if (dropdownInput) {
        // Wait for next tick to ensure dropdown-input is initialized
        setTimeout(() => {
          if (typeof dropdownInput.update === 'function') {
            dropdownInput.update();
          }
        }, 0);
      }
    }

    setupStoreSelector() {
      if (!this.storeSelect) return;

      // this.storeSelect is already the <select> element
      const select = this.storeSelect;
      if (!select || select.tagName !== 'SELECT') return;

      select.addEventListener('change', () => {
        const storeName = select.value;
        this.selectedStore = this.stores.find(s => s.store_name === storeName);
        
        if (this.selectedStore) {
          this.storeAddressInput.value = this.selectedStore.address;
          this.updateAvailableDates();
          
          // If a date is already selected, update available times for that date
          if (this.dateInput && this.dateInput.value) {
            this.updateAvailableTimes();
          } else {
            // Clear date and time when store changes and no date is selected
            if (this.flatpickrInstance) {
              this.flatpickrInstance.clear();
            } else if (this.dateInput) {
              this.dateInput.value = '';
            }
            this.showSelectDateFirstMessage();
          }
        } else {
          this.storeAddressInput.value = '';
          this.clearDateRestrictions();
          if (this.flatpickrInstance) {
            this.flatpickrInstance.clear();
          } else if (this.dateInput) {
            this.dateInput.value = '';
          }
          this.showSelectDateFirstMessage();
        }
      });
    }

    getDateFormatForCountry(countryCode) {
      // Normalize country code to uppercase
      const code = (countryCode || '').toUpperCase();
      
      // Return date format based on country code
      if (code === 'US') return 'm/d/Y';
      if (code === 'IT' || code === 'FR' || code === 'ES' || code === 'PT' || 
          code === 'GB' || code === 'UK' || code === 'IE' || code === 'NL' || 
          code === 'BE' || code === 'LU' || code === 'GR' || code === 'CY' || code === 'MT') {
        return 'd/m/Y';
      }
      if (code === 'DE' || code === 'AT' || code === 'CH') return 'd.m.Y';
      if (code === 'JP' || code === 'CN' || code === 'KR' || code === 'TW') return 'Y/m/d';
      
      // Default ISO format
      return 'Y-m-d';
    }

    getDateAsISO() {
      // Get date from Flatpickr instance (returns Date object) or parse from input value
      if (this.flatpickrInstance && this.flatpickrInstance.selectedDates.length > 0) {
        const date = this.flatpickrInstance.selectedDates[0];
        // Convert to ISO format (YYYY-MM-DD)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      // Fallback: try to parse the input value
      if (this.dateInput.value) {
        const date = new Date(this.dateInput.value);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }
      return this.dateInput.value || '';
    }

    getDateAsDateObject() {
      // Get Date object from Flatpickr or parse from input
      if (this.flatpickrInstance && this.flatpickrInstance.selectedDates.length > 0) {
        return this.flatpickrInstance.selectedDates[0];
      }
      // Fallback: try to parse
      if (this.dateInput.value) {
        // Try parsing with Flatpickr's format
        const date = this.flatpickrInstance?.parseDate(this.dateInput.value, this.getDateFormatForCountry(this.countryCode));
        if (date) return date;
        // Last resort: try standard Date parsing
        return new Date(this.dateInput.value);
      }
      return null;
    }

    setupDateSelector() {
      if (!this.dateInput) return;

      // Wait for Flatpickr to be available
      if (typeof flatpickr === 'undefined') {
        // Retry after a short delay
        setTimeout(() => this.setupDateSelector(), 100);
        return;
      }

      // Initialize Flatpickr
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Get date format based on country
      const dateFormat = this.getDateFormatForCountry(this.countryCode);

      this.flatpickrInstance = flatpickr(this.dateInput, {
        minDate: today,
        dateFormat: dateFormat,
        onChange: (selectedDates, dateStr) => {
          if (dateStr) {
            this.updateAvailableTimes();
          } else {
            this.showSelectDateFirstMessage();
          }
        },
        onClose: (selectedDates, dateStr) => {
          // Ensure time is updated when date picker closes
          if (dateStr) {
            this.updateAvailableTimes();
          }
        }
      });
    }

    updateAvailableDates() {
      if (!this.selectedStore || !this.dateInput) return;

      // Get available days from store
      const availableDays = Object.keys(this.selectedStore).filter(key =>
        ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].includes(key)
      );

      const closedDays = Array.isArray(this.selectedStore.closed_days)
        ? this.selectedStore.closed_days
        : [];
      const closedSet = new Set(closedDays);

      if (availableDays.length === 0) {
        if (this.flatpickrInstance) {
          this.flatpickrInstance.set('disable', []);
          this.flatpickrInstance.input.disabled = true;
        } else {
          this.dateInput.disabled = true;
        }
        return;
      }

      if (this.flatpickrInstance) {
        this.flatpickrInstance.input.disabled = false;
        // Configure Flatpickr to disable dates that don't fall on available days
        // or that are in the store's closed_days list (MM-DD, cross-year).
        this.flatpickrInstance.set('disable', [
          (date) => {
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayOfWeek = dayNames[date.getDay()];
            if (!availableDays.includes(dayOfWeek)) {
              return true;
            }
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const key = `${month}-${day}`;
            return closedSet.has(key);
          }
        ]);
      } else {
        this.dateInput.disabled = false;
      }

      // Store available days for validation
      this.dateInput.dataset.availableDays = JSON.stringify(availableDays);
    }

    updateAvailableTimes() {
      if (!this.selectedStore || !this.dateInput || !this.timeSelect) {
        this.showSelectDateFirstMessage();
        return;
      }
      
      const dateObj = this.getDateAsDateObject();
      if (!dateObj) {
        this.showSelectDateFirstMessage();
        return;
      }

      // Respect store-level closed days (cross-year, MM-DD)
      const closedDays = Array.isArray(this.selectedStore.closed_days)
        ? this.selectedStore.closed_days
        : [];
      const closedSet = new Set(closedDays);
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const key = `${month}-${day}`;
      if (closedSet.has(key)) {
        this.showNoTimesAvailableMessage();
        return;
      }

      // Get day of week from selected date
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayOfWeek = dayNames[dateObj.getDay()];

      // Get available times for this day
      const availableTimes = this.selectedStore[dayOfWeek] || [];
      
      if (availableTimes.length === 0) {
        this.showNoTimesAvailableMessage();
        return;
      }

      this.populateTimeOptions(availableTimes);
    }

    showSelectDateFirstMessage() {
      if (!this.timeSelect) return;
      const select = this.timeSelect;
      if (!select || select.tagName !== 'SELECT') return;

      select.innerHTML = '';
      const option = document.createElement('option');
      option.value = '';
      option.textContent = this.getSelectDateFirstText();
      option.setAttribute('data-placeholder', 'true');
      option.disabled = true;
      select.appendChild(option);
      select.value = '';

      // Update custom dropdown
      const dropdownInput = select.closest('dropdown-input');
      if (dropdownInput) {
        setTimeout(() => {
          if (typeof dropdownInput.update === 'function') {
            dropdownInput.update();
          }
        }, 0);
      }
    }

    showNoTimesAvailableMessage() {
      if (!this.timeSelect) return;
      const select = this.timeSelect;
      if (!select || select.tagName !== 'SELECT') return;

      select.innerHTML = '';
      const option = document.createElement('option');
      option.value = '';
      option.textContent = this.getNoTimesAvailableText();
      option.setAttribute('data-placeholder', 'true');
      option.disabled = true;
      select.appendChild(option);
      select.value = '';

      // Update custom dropdown
      const dropdownInput = select.closest('dropdown-input');
      if (dropdownInput) {
        setTimeout(() => {
          if (typeof dropdownInput.update === 'function') {
            dropdownInput.update();
          }
        }, 0);
      }
    }

    getSelectDateFirstText() {
      // Try to get from translation or use default
      if (window.translationStrings && window.translationStrings.selectDateFirst) {
        return window.translationStrings.selectDateFirst;
      }
      // Fallback to English
      return 'Please select a date first';
    }

    getNoTimesAvailableText() {
      // Try to get from translation or use default
      if (window.translationStrings && window.translationStrings.noTimesAvailable) {
        return window.translationStrings.noTimesAvailable;
      }
      // Fallback to English
      return 'No times available for the selected date';
    }

    getTimeSelectPlaceholderText() {
      // Get placeholder text for time select when times are available
      // Try to get from existing option or use default
      if (this.timeSelect) {
        const existingPlaceholder = this.timeSelect.querySelector('option[value=""]:not([data-placeholder])');
        if (existingPlaceholder && existingPlaceholder.textContent && !existingPlaceholder.disabled) {
          return existingPlaceholder.textContent;
        }
      }
      // Fallback - try to get from translation
      if (window.translationStrings && window.translationStrings.timePlaceholder) {
        return window.translationStrings.timePlaceholder;
      }
      return 'Select time';
    }

    populateTimeOptions(times) {
      if (!this.timeSelect) return;

      // this.timeSelect is already the <select> element
      const select = this.timeSelect;
      if (!select || select.tagName !== 'SELECT') return;

      // Store current value to preserve selection if still valid
      const currentValue = select.value;
      
      // Clear existing options
      select.innerHTML = '';

      // Add placeholder option (for "Select time" when times are available)
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this.getTimeSelectPlaceholderText();
      select.appendChild(placeholder);

      // Add time options
      times.forEach(time => {
        const option = document.createElement('option');
        option.value = time;
        option.textContent = time;
        // Preserve selection if still valid
        if (time === currentValue) {
          option.selected = true;
        }
        select.appendChild(option);
      });

      // If current value is no longer valid, reset to placeholder
      if (currentValue && !times.includes(currentValue)) {
        select.value = '';
      }

      // Update custom dropdown if it exists and is initialized
      const dropdownInput = select.closest('dropdown-input');
      if (dropdownInput) {
        // Wait for next tick to ensure dropdown-input is initialized
        setTimeout(() => {
          if (typeof dropdownInput.update === 'function') {
            dropdownInput.update();
          }
        }, 0);
      }
    }


    clearTimeOptions() {
      // Show "select date first" message instead of clearing
      this.showSelectDateFirstMessage();
    }

    clearDateRestrictions() {
      if (this.dateInput) {
        this.dateInput.removeAttribute('data-available-days');
      }
    }

    setupTimeSelector() {
      // Time options are populated dynamically based on selected date
    }

    setupValidation() {
      // Custom validation is handled in setupFormSubmit
      // form-validate will handle HTML5 validation first
    }


    validateCustomFields() {
      let isValid = true;

      // Validate store is selected
      if (!this.selectedStore) {
        this.showFieldError(this.storeSelect, window.translationStrings?.errorSelectStore || 'Please select a store');
        isValid = false;
      }

      // Validate date is on an available day
      if (this.selectedStore && this.dateInput.value) {
        const dateObj = this.getDateAsDateObject();
        if (!dateObj) {
          this.showFieldError(this.dateInput, window.translationStrings?.errorSelectValidDate || 'Please select a valid date');
          isValid = false;
        } else {
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const dayOfWeek = dayNames[dateObj.getDay()];
        
          if (!this.selectedStore[dayOfWeek] || this.selectedStore[dayOfWeek].length === 0) {
            this.showFieldError(this.dateInput, window.translationStrings?.errorDateNotAvailable || 'Selected date is not available for this store');
            isValid = false;
          }
        }
      }

      // Validate time is in available slots
      if (this.selectedStore && this.dateInput.value && this.timeSelect) {
        const dateObj = this.getDateAsDateObject();
        if (!dateObj) {
          this.showFieldError(this.timeSelect, window.translationStrings?.errorSelectDateFirst || 'Please select a valid date first');
          isValid = false;
        } else {
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const dayOfWeek = dayNames[dateObj.getDay()];
        const availableTimes = this.selectedStore[dayOfWeek] || [];
        // this.timeSelect is already the <select> element
        const selectedTime = this.timeSelect.value;
        const selectedOption = this.timeSelect.options[this.timeSelect.selectedIndex];
        
          // Check if selected option is a placeholder (disabled)
          if (!selectedTime || (selectedOption && selectedOption.disabled)) {
            this.showFieldError(this.timeSelect, window.translationStrings?.errorSelectTime || 'Please select a time');
            isValid = false;
          } else if (selectedTime && !availableTimes.includes(selectedTime)) {
            this.showFieldError(this.timeSelect, window.translationStrings?.errorTimeNotAvailable || 'Selected time is not available');
            isValid = false;
          }
        }
      } else if (this.dateInput.value && this.timeSelect) {
        // Date is selected but no store - should not happen, but validate anyway
        const selectedOption = this.timeSelect.options[this.timeSelect.selectedIndex];
        if (!this.timeSelect.value || (selectedOption && selectedOption.disabled)) {
          this.showFieldError(this.timeSelect, window.translationStrings?.errorSelectTime || 'Please select a time');
          isValid = false;
        }
      }

      return isValid;
    }

    showFieldError(fieldElement, message) {
      // For select elements, find the dropdown-input wrapper
      // For input elements, find the data-input-wrapper
      let wrapper = null;
      let input = null;

      if (fieldElement.tagName === 'SELECT') {
        // Select is inside dropdown-input
        const dropdownInput = fieldElement.closest('dropdown-input');
        if (dropdownInput) {
          wrapper = dropdownInput;
          input = fieldElement;
        } else {
          // Fallback: try to find data-input-wrapper
          wrapper = fieldElement.closest('[data-input-wrapper]');
          input = fieldElement;
        }
      } else {
        // Input element
        wrapper = fieldElement.closest('[data-input-wrapper]');
        input = wrapper ? wrapper.querySelector('input, select') : fieldElement;
      }

      if (!wrapper) {
        // If no wrapper found, try to create error message on the element itself
        console.warn('Could not find wrapper for field error:', fieldElement);
        return;
      }

      wrapper.classList.add('has-error');
      let errorMessage = wrapper.querySelector('[data-message]');
      
      if (!errorMessage) {
        errorMessage = document.createElement('span');
        errorMessage.className = 'field__message';
        errorMessage.setAttribute('data-message', '');
        wrapper.appendChild(errorMessage);
      }
      
      errorMessage.textContent = message;
      errorMessage.classList.remove('hidden');
      
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        if (input.setCustomValidity) {
          input.setCustomValidity(message);
        }
      }
    }

    clearFieldError(fieldElement) {
      // For select elements, find the dropdown-input wrapper
      // For input elements, find the data-input-wrapper
      let wrapper = null;
      let input = null;

      if (fieldElement.tagName === 'SELECT') {
        // Select is inside dropdown-input
        const dropdownInput = fieldElement.closest('dropdown-input');
        if (dropdownInput) {
          wrapper = dropdownInput;
          input = fieldElement;
        } else {
          // Fallback: try to find data-input-wrapper
          wrapper = fieldElement.closest('[data-input-wrapper]');
          input = fieldElement;
        }
      } else {
        // Input element
        wrapper = fieldElement.closest('[data-input-wrapper]');
        input = wrapper ? wrapper.querySelector('input, select') : fieldElement;
      }

      if (!wrapper) return;

      wrapper.classList.remove('has-error');
      const errorMessage = wrapper.querySelector('[data-message]');
      if (errorMessage) {
        errorMessage.classList.add('hidden');
        errorMessage.textContent = '';
      }
      
      if (input) {
        input.setAttribute('aria-invalid', 'false');
        if (input.setCustomValidity) {
          input.setCustomValidity('');
        }
      }
    }

    setupFormSubmit() {
      // Intercept submit in capture phase before form-validate.
      this.form.addEventListener('submit', async (event) => {
        // First validate readonly fields (form-validate does this, but we need to do it here
        // since we intercept in capture phase before form-validate)
        const formValidate = this.closest('form-validate');
        if (formValidate && typeof formValidate.validateReadonlyFields === 'function') {
          formValidate.validateReadonlyFields();
        }
        
        // Then check HTML5 validation using reportValidity which shows errors
        if (!this.form.reportValidity()) {
          // Form is invalid, prevent default to stop submission
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setRequiredFieldErrors();
          return;
        }

        // If HTML5 validation passes, prevent default to handle AJAX submit
        event.preventDefault();
        event.stopImmediatePropagation();
        
        // Now run custom validations
        if (!this.validateCustomFields()) {
          return;
        }

        // Disable submit button and show submitting text
        if (this.submitBtn) {
          this.submitBtn.disabled = true;
          // Save original text if not already saved
          if (!this.submitBtn.dataset.originalText) {
            this.submitBtn.dataset.originalText = this.submitBtn.textContent;
          }
          this.submitBtn.textContent = window.translationStrings?.submitting || 'Submitting...';
        }

        // Hide previous messages
        this.hideMessages();

        // Prepare form data
        const languageCodeInput = this.form.querySelector('input[name="language_code"]');
        const privacyCheckbox = this.form.querySelector('#privacy');
        
        // Convert date to d/m/Y format (backend requirement)
        let appointmentDate = '';
        if (this.flatpickrInstance && this.flatpickrInstance.selectedDates.length > 0) {
          const date = this.flatpickrInstance.selectedDates[0];
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          appointmentDate = `${day}/${month}/${year}`;
        } else if (this.dateInput.value) {
          // Fallback: try to parse the displayed date and convert to d/m/Y
          const dateObj = this.getDateAsDateObject();
          if (dateObj) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            appointmentDate = `${day}/${month}/${year}`;
          } else {
            appointmentDate = this.dateInput.value; // Last resort
          }
        }
        
        const notesInput = this.form.querySelector('#AppointmentForm-Notes');
        const formData = {
          first_name: this.form.querySelector('#AppointmentForm-FirstName').value.trim(),
          last_name: this.form.querySelector('#AppointmentForm-LastName').value.trim(),
          email: this.form.querySelector('#AppointmentForm-Email').value.trim(),
          store_name: this.selectedStore.store_name,
          store_address: this.selectedStore.address,
          appointment_date: appointmentDate,
          appointment_time: this.timeSelect.value,
          language_code: languageCodeInput ? languageCodeInput.value : 'en',
          privacy: privacyCheckbox ? privacyCheckbox.checked : false,
          notes: notesInput ? notesInput.value.trim() : ''
        };

        try {
          const response = await fetch(this.apiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData)
          });

          const data = await response.json();

          // Backend returns: {message: 'email_sent', errors: []} on success
          if (response.ok && data.message === 'email_sent') {
            this.showSuccess(window.translationStrings?.successBooked || 'Appointment booked successfully');
            this.form.reset();
            // Reset Flatpickr if initialized
            if (this.flatpickrInstance) {
              this.flatpickrInstance.clear();
            }
            this.selectedStore = null;
            this.storeAddressInput.value = '';
            this.showSelectDateFirstMessage();
          } else {
            this.handleError(response.status, data);
          }
        } catch (error) {
          console.error('Error submitting form:', error);
          this.showError(window.translationStrings?.errorNetwork || 'Network error. Please try again.');
        } finally {
          // Always restore submit button text (works for both success and error cases)
          this.restoreSubmitButton();
        }
      }, true);
    }

    setRequiredFieldErrors() {
      const validator = this.closest('form-validate');
      if (!validator || typeof validator.setFieldValidity !== 'function') return;

      for (const el of this.form.elements) {
        if (el.hasAttribute && el.hasAttribute('required') && el.validity && !el.validity.valid) {
          validator.setFieldValidity(el);
        }
      }
    }

    handleError(status, data) {
      switch (status) {
        case 422:
          // Backend validation error: {message: 'fail validation', errors: {field: ['message']}}
          this.handleValidationError(data);
          break;
        case 500:
          // Backend returns: {message: 'admin_email_send_failed' or 'customer_email_send_failed', errors: []}
          if (data.message === 'admin_email_send_failed' || data.message === 'customer_email_send_failed') {
            this.showError(window.translationStrings?.errorServer || 'Server error. Please try again later.');
          } else {
            this.showError(data.message || window.translationStrings?.errorServer || 'Server error. Please try again later.');
          }
          break;
        default:
          this.showError(data.message || window.translationStrings?.errorGeneric || 'An error occurred. Please try again.');
      }
    }

    handleValidationError(data) {
      // Backend returns: {message: 'fail validation', errors: {field: ['message'], ...}}
      if (data.errors && typeof data.errors === 'object') {
        // Handle multiple field errors
        let hasFieldError = false;
        for (const [field, messages] of Object.entries(data.errors)) {
          if (Array.isArray(messages) && messages.length > 0) {
            const fieldId = `AppointmentForm-${this.capitalizeFirst(field)}`;
            const fieldElement = this.form.querySelector(`#${fieldId}`);
            if (fieldElement) {
              this.showFieldError(fieldElement.closest('[data-input-wrapper]') || fieldElement, messages[0]);
              hasFieldError = true;
            }
          }
        }
        // If no specific field errors were shown, show generic error
        if (!hasFieldError) {
          this.showError(window.translationStrings?.errorValidationFailed || 'Validation failed');
        }
      } else {
        this.showError(data.message || window.translationStrings?.errorValidationFailed || 'Validation failed');
      }
    }

    capitalizeFirst(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    showError(message) {
      if (!this.formErrors) return;

      this.formErrors.style.display = 'block';
      const list = this.formErrors.querySelector('ul');
  
      
      if (list) {
        list.innerHTML = `<li>${message}</li>`;
      }

      this.formErrors.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.formErrors.focus();
    }

    showSuccess(message) {
      if (!this.formSuccess) return;

      // Hide the form
      if (this.form) {
        this.form.style.display = 'none';
      }

      // Show success message
      this.formSuccess.textContent = message;
      this.formSuccess.style.display = 'block';
      this.formSuccess.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.formSuccess.focus();
    }

    hideMessages() {
      if (this.formErrors) {
        this.formErrors.style.display = 'none';
        const list = this.formErrors.querySelector('ul');
        if (list) list.innerHTML = '';
      }
      if (this.formSuccess) {
        this.formSuccess.style.display = 'none';
      }
    }

    restoreSubmitButton() {
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        // Restore original text
        const originalText = this.submitBtn.dataset.originalText || window.translationStrings?.submit || 'Book appointment';
        this.submitBtn.textContent = originalText;
      }
    }


    connectedCallback() {
      this.init();
    }
  }

  customElements.define('appointment-booking-form', AppointmentBookingForm);
}
